import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  EnvelopeSimple,
  PaperPlaneTilt,
  Phone,
  Baby,
  Calendar,
  ChatsCircle,
  CheckCircle,
  Archive,
  ArrowCounterClockwise,
  UserCircle,
  ArrowRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  getInquiry,
  updateInquiryNotes,
  updateInquiryStatus,
  listOrgAdmins,
  statusMeta,
  INQUIRY_STATUSES,
} from "@/lib/inquiries";
import {
  QUICK_REPLY_TEMPLATES,
  buildMailtoUrl,
  inquiryAgeBucket,
  AGE_TONE,
  formatAgeShort,
} from "@/lib/inquiryFunnel";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import InquiryAssigneePicker from "@/components/admin/InquiryAssigneePicker";
import { promoteInquiryToAdmission } from "@/lib/admissions";

/**
 * Admin · single inquiry detail.
 *
 * Left column: the submitter's message + metadata (date, phone, email).
 * Right column: status flow (new → contacted → archived) + notes.
 *
 * Notes are saved on explicit "Save note" click so a stray tab-out
 * doesn't persist a half-thought. Small UX win over autosave here
 * since notes are informational not transactional.
 */
export default function AdminInquiryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [row, setRow]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [admins, setAdmins] = useState([]);
  const [promoting, setPromoting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getInquiry(id);
      setRow(r);
      setNotesDraft(r?.notes || "");
    } catch (err) {
      toast.error(err?.message || "Could not load inquiry");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  // Pre-load admins so the picker and the inline "Assigned to" chip can
  // resolve the uuid to a readable name without a second round-trip.
  useEffect(() => {
    if (!profile?.organization_id) return;
    let alive = true;
    (async () => {
      const list = await listOrgAdmins(profile.organization_id);
      if (alive) setAdmins(list);
    })();
    return () => { alive = false; };
  }, [profile?.organization_id]);

  const currentAssignee = useMemo(() => {
    if (!row?.assigned_to) return null;
    const hit = admins.find((a) => a.profile_id === row.assigned_to);
    if (hit) return hit;
    // Fall back to a stub so the UI still reads sensibly if the admin row
    // hasn't loaded yet (or was demoted but still on the inquiry).
    return {
      profile_id: row.assigned_to,
      full_name: row.assigned_to === profile?.id
        ? (profile.full_name || "You")
        : "Assigned admin",
      email: null,
    };
  }, [row?.assigned_to, admins, profile?.id, profile?.full_name]);

  const onStatusChange = async (next) => {
    if (!row || row.status === next) return;
    setSaving(true);
    try {
      const updated = await updateInquiryStatus(row.id, next);
      setRow(updated);
      toast.success(`Marked as ${next}`);
    } catch (err) {
      toast.error(err?.message || "Could not update status");
    } finally {
      setSaving(false);
    }
  };

  const onSaveNotes = async () => {
    setSaving(true);
    try {
      const updated = await updateInquiryNotes(row.id, notesDraft);
      setRow(updated);
      toast.success("Note saved");
    } catch (err) {
      toast.error(err?.message || "Could not save note");
    } finally {
      setSaving(false);
    }
  };

  const onPromote = async () => {
    if (!row || promoting) return;
    setPromoting(true);
    try {
      const admission = await promoteInquiryToAdmission({ inquiryId: row.id });
      if (!admission?.id) throw new Error("The admission case could not be created.");
      toast.success("Admission case is ready for review.");
      navigate(`/admin/enrollment/${admission.id}`);
    } catch (err) {
      toast.error(err?.message || "Could not create an admission case");
    } finally {
      setPromoting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
      </div>
    );
  }

  if (!row) {
    return (
      <div data-testid="admin-inquiry-missing" className="py-16 text-center">
        <p className="font-heading text-xl font-semibold text-neutral-900">
          Inquiry not found
        </p>
        <Link
          to="/admin/inquiries"
          className="mt-3 inline-block text-sm font-semibold text-primary-hover hover:underline"
        >
          Back to inquiries
        </Link>
      </div>
    );
  }

  const s = statusMeta(row.status);
  const ageBucket = inquiryAgeBucket(row);
  const ageTone = AGE_TONE[ageBucket];

  return (
    <div data-testid="admin-inquiry-detail" data-id={row.id} data-status={row.status} data-age-bucket={ageBucket} className="space-y-6">
      <nav className="-mt-2 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => navigate("/admin/inquiries")}
          className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 transition hover:bg-neutral-50"
        >
          <ArrowLeft size={12} weight="bold" />
          All inquiries
        </button>
        <div className="flex items-center gap-2">
          {ageTone && (
            <span
              data-testid="admin-inquiry-age-pill"
              data-age-bucket={ageBucket}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${ageTone.pill}`}
            >
              {ageTone.label} · {formatAgeShort(row.created_at)}
            </span>
          )}
          <span
            data-testid="admin-inquiry-status-pill"
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${s.tone}`}
          >
            {s.label}
          </span>
        </div>
      </nav>

      <header>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Enrollment inquiry
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          {row.parent_name}
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          Received {new Date(row.created_at).toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" })}
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-[1.25fr_0.75fr]">
        {/* LEFT: contact + message */}
        <section className="space-y-5">
          <article className="rounded-2xl border border-neutral-200 bg-white p-6">
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
              Contact
            </p>
            <ul className="mt-3 space-y-2.5 text-sm text-neutral-800">
              <Row icon={EnvelopeSimple}>
                <a href={`mailto:${row.parent_email}`} className="hover:underline" data-testid="admin-inquiry-email">
                  {row.parent_email}
                </a>
              </Row>
              {row.parent_phone && (
                <Row icon={Phone}>
                  <a href={`tel:${row.parent_phone}`} className="hover:underline" data-testid="admin-inquiry-phone">
                    {row.parent_phone}
                  </a>
                </Row>
              )}
              {row.child_age && (
                <Row icon={Baby}>
                  <span data-testid="admin-inquiry-child-age">{row.child_age}</span>
                </Row>
              )}
              <Row icon={Calendar}>
                <span>{new Date(row.created_at).toLocaleDateString()}</span>
              </Row>
            </ul>
          </article>

          <article className="rounded-2xl border border-neutral-200 bg-white p-6">
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
              Their message
            </p>
            <blockquote
              data-testid="admin-inquiry-message"
              className="mt-3 whitespace-pre-wrap border-l-[3px] border-primary/40 bg-primary/[0.04] px-4 py-3 text-sm leading-relaxed text-neutral-800"
            >
              {row.message}
            </blockquote>
            <div className="mt-4 flex flex-wrap gap-2 text-xs text-neutral-500">
              {row.source && (
                <span className="rounded-full bg-neutral-100 px-2 py-0.5 uppercase tracking-wide">
                  Source: {row.source.replace(/_/g, " ")}
                </span>
              )}
            </div>
          </article>

          <QuickReplyCard row={row} />
        </section>

        {/* RIGHT: status + notes */}
        <aside className="space-y-5">
          <article
            data-testid="admin-inquiry-promote"
            className="rounded-2xl border border-primary/25 bg-primary/5 p-6"
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-primary-hover">
              Start admissions work
            </p>
            <p className="mt-2 font-heading text-base font-semibold text-neutral-900">
              Turn this lead into an admission case
            </p>
            <p className="mt-1 text-sm leading-relaxed text-neutral-700">
              This preserves the original inquiry and creates one canonical case
              for tours, application review, waitlist decisions, and enrollment.
            </p>
            <button
              type="button"
              onClick={onPromote}
              disabled={promoting}
              data-testid="admin-inquiry-promote-button"
              className="mt-4 inline-flex min-h-10 items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-hover disabled:opacity-60"
            >
              {promoting ? "Creating…" : "Create admission case"}
              {!promoting && <ArrowRight size={14} weight="bold" />}
            </button>
          </article>

          <article
            data-testid="admin-inquiry-assignee-article"
            className="rounded-2xl border border-neutral-200 bg-white p-6"
          >
            <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
              <UserCircle size={12} weight="duotone" />
              Assigned to
            </p>
            <div className="mt-3">
              <InquiryAssigneePicker
                inquiryId={row.id}
                currentAssignee={currentAssignee}
                onChange={(updated) => setRow(updated)}
                disabled={saving}
              />
            </div>
            <p className="mt-3 text-[11px] leading-relaxed text-neutral-500">
              The assignee owns the follow-up for this lead. Unassigned inquiries
              stay in the team queue for anyone to pick up.
            </p>
          </article>

          <article className="rounded-2xl border border-neutral-200 bg-white p-6">
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
              Status flow
            </p>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="admin-inquiry-status-buttons">
              <StatusButton
                active={row.status === "new"}
                onClick={() => onStatusChange("new")}
                icon={ArrowCounterClockwise}
                label="New"
                testid="admin-inquiry-status-new"
                disabled={saving}
              />
              <StatusButton
                active={row.status === "contacted"}
                onClick={() => onStatusChange("contacted")}
                icon={CheckCircle}
                label="Contacted"
                testid="admin-inquiry-status-contacted"
                disabled={saving}
              />
              <StatusButton
                active={row.status === "archived"}
                onClick={() => onStatusChange("archived")}
                icon={Archive}
                label="Archived"
                testid="admin-inquiry-status-archived"
                disabled={saving}
              />
            </div>
            <p className="mt-4 text-[11px] text-neutral-500">
              Moving to <strong>Contacted</strong> signals a teammate has replied.
              <br />
              <strong>Archived</strong> hides it from the default queue.
            </p>
          </article>

          <article className="rounded-2xl border border-neutral-200 bg-white p-6">
            <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
              <ChatsCircle size={12} weight="duotone" />
              Internal notes
            </p>
            <textarea
              data-testid="admin-inquiry-notes"
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              placeholder="Visible only to admins. Add call notes, tour scheduled, etc."
              rows={6}
              className="mt-3 w-full resize-y rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm placeholder:text-neutral-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              disabled={saving}
            />
            <div className="mt-3 flex items-center justify-between">
              <p className="text-[11px] text-neutral-400">
                {row.updated_at
                  ? `Last updated ${new Date(row.updated_at).toLocaleString()}`
                  : ""}
              </p>
              <button
                type="button"
                onClick={onSaveNotes}
                disabled={saving || notesDraft === (row.notes || "")}
                data-testid="admin-inquiry-notes-save"
                className="rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-primary-hover disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save note"}
              </button>
            </div>
          </article>
        </aside>
      </div>

      {/* Quick-links to jump filter views */}
      <div className="flex flex-wrap gap-2 pt-2">
        {INQUIRY_STATUSES.map((st) => (
          <Link
            key={st.key}
            to={`/admin/inquiries?status=${st.key}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1 text-[11px] font-semibold text-neutral-600 transition hover:bg-neutral-50"
          >
            See {st.label.toLowerCase()} inquiries →
          </Link>
        ))}
      </div>
    </div>
  );
}

function Row({ icon: Icon, children }) {
  return (
    <li className="flex items-center gap-3">
      <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-primary/10 text-primary-hover">
        <Icon size={14} weight="duotone" />
      </span>
      <div>{children}</div>
    </li>
  );
}

function StatusButton({ active, onClick, icon: Icon, label, testid, disabled }) {
  return (
    <button
      type="button"
      data-testid={testid}
      data-active={active ? "true" : "false"}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
        active
          ? "bg-primary text-white shadow-sm"
          : "border border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300 hover:bg-neutral-50"
      } disabled:opacity-60`}
    >
      <Icon size={12} weight="bold" />
      {label}
    </button>
  );
}


/**
 * Quick-reply templates — three short, friendly options that cover the
 * vast majority of first replies. Each opens the admin's default mail
 * client (Apple Mail, Gmail web, Outlook, etc.) with the subject and
 * body pre-filled. They review and personalise before sending — we
 * intentionally never auto-send, so this stays a calm assistive tool
 * and not a parallel sales engine.
 */
function QuickReplyCard({ row }) {
  const branding = useBranding();
  const orgName = branding?.organization?.display_name;
  if (!row?.parent_email) return null;
  return (
    <article
      data-testid="admin-inquiry-quick-replies"
      className="rounded-2xl border border-neutral-200 bg-white p-6"
    >
      <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
        <PaperPlaneTilt size={12} weight="duotone" />
        Quick reply templates
      </p>
      <p className="mt-1 text-[12px] leading-relaxed text-neutral-500">
        Open in your mail app — the subject and body are pre-filled. You
        review and personalise before sending. Nothing leaves the system here.
      </p>
      <ul className="mt-3 space-y-2">
        {QUICK_REPLY_TEMPLATES.map((tpl) => (
          <li key={tpl.key}>
            <a
              data-testid={`admin-inquiry-quick-reply-${tpl.key}`}
              href={buildMailtoUrl(tpl, { row, organizationName: orgName })}
              className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-3 transition-all hover:-translate-y-[1px] hover:border-primary/30 hover:bg-primary/5"
            >
              <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary-hover">
                <PaperPlaneTilt size={12} weight="duotone" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-heading text-sm font-semibold text-neutral-900">{tpl.label}</p>
                <p className="mt-0.5 text-[11px] text-neutral-500">{tpl.blurb}</p>
              </div>
            </a>
          </li>
        ))}
      </ul>
    </article>
  );
}
